import { DataTable } from "@circleco/compass/components/DataTable";
import { EmptyState } from "@circleco/compass/components/EmptyState";
import { get, noop } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ComponentPropsWithRef } from "react";

import { Wrapper } from "./components/Wrapper";
import { DEFAULT_PAGE_SIZE } from "./constants";
import { toArray, toRecord, toNumber } from "./utils";

type DataTableCompassProps<TData> = ComponentPropsWithRef<
  typeof DataTable<TData>
>;

type OverriddenProps =
  | "data"
  | "loading"
  | "manualPagination"
  | "onPageChange"
  | "pagination"
  | "rowCount"
  | "state";

interface InfiniteQueryLike {
  data?: { pages?: unknown[] };
  fetchNextPage?: () => Promise<unknown>;
  fetchPreviousPage?: () => Promise<unknown>;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  isFetching?: boolean;
  isFetchingNextPage?: boolean;
  isFetchingPreviousPage?: boolean;
  isLoading?: boolean;
  isPending?: boolean;
}

export type InfiniteQueryDataTableCompassProps<TData> = Omit<
  DataTableCompassProps<TData>,
  OverriddenProps
> & {
  infiniteQuery: InfiniteQueryLike | null | undefined;
  initialPage?: number;
  onChangePage?: (page: number) => void;
  pageDataPath?: string;
  pageNumberPath?: string;
  totalCountPath?: string;
  sorting?: Array<{ id: string; desc: boolean }>;
};

export const InfiniteQueryDataTableCompass = <TData,>({
  infiniteQuery,
  initialPage = 1,
  onChangePage = noop,
  pageDataPath = "records",
  pageNumberPath = "page",
  totalCountPath = "count",
  sorting,
  emptyOverlay,
  emptyStateProps,
  ...rest
}: InfiniteQueryDataTableCompassProps<TData>) => {
  const safeInitial = initialPage > 0 ? initialPage : 1;
  const [pageNumber, setPageNumber] = useState(safeInitial);

  useEffect(() => {
    if (initialPage > 0) setPageNumber(initialPage);
  }, [initialPage]);

  const data = infiniteQuery?.data;
  const fetchNextPage = infiniteQuery?.fetchNextPage;
  const fetchPreviousPage = infiniteQuery?.fetchPreviousPage;
  const hasNextPage = infiniteQuery?.hasNextPage ?? false;
  const hasPreviousPage = infiniteQuery?.hasPreviousPage ?? false;
  const isLoading =
    infiniteQuery?.isPending ?? infiniteQuery?.isLoading ?? false;
  const isFetching = infiniteQuery?.isFetching ?? false;
  const isFetchingNextPage = infiniteQuery?.isFetchingNextPage ?? false;
  const isFetchingPreviousPage = infiniteQuery?.isFetchingPreviousPage ?? false;

  const pages = useMemo(() => {
    const raw = data?.pages;
    return raw != null ? toArray<Record<string, unknown>>(raw) : [];
  }, [data?.pages]);

  const getPageNumber = useCallback(
    (page: Record<string, unknown>) => toNumber(get(page, pageNumberPath, -1)),
    [pageNumberPath],
  );

  const { totalCount, pageSize } = useMemo(() => {
    const firstPage = toRecord(pages[0]);
    return {
      totalCount: toNumber(get(firstPage, totalCountPath, 0)),
      pageSize: toNumber(get(firstPage, "per_page")) || DEFAULT_PAGE_SIZE,
    };
  }, [pages, totalCountPath]);

  const currentPageData = useMemo(() => {
    const currentPage = pages.find(p => getPageNumber(p) === pageNumber);
    const pageRec = toRecord(currentPage);
    const rawData: unknown = get(pageRec, pageDataPath, []);
    return toArray<TData>(rawData);
  }, [pages, pageNumber, pageDataPath, getPageNumber]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      const isPageAlreadyFetched = pages.some(
        (page: Record<string, unknown>) => getPageNumber(page) === newPage,
      );

      if (isPageAlreadyFetched) {
        setPageNumber(newPage);
        onChangePage(newPage);
        return;
      }

      const isGoingForward = newPage > pageNumber;
      const isGoingBackward = newPage < pageNumber;

      const performFetch = async (): Promise<void> => {
        if (isGoingForward && hasNextPage && fetchNextPage != null) {
          await fetchNextPage();
        } else if (
          isGoingBackward &&
          hasPreviousPage &&
          fetchPreviousPage != null
        ) {
          await fetchPreviousPage();
        } else {
          return;
        }
        setPageNumber(newPage);
        onChangePage(newPage);
      };

      performFetch().catch(noop);
    },
    [
      pages,
      pageNumber,
      hasNextPage,
      hasPreviousPage,
      fetchNextPage,
      fetchPreviousPage,
      getPageNumber,
      onChangePage,
    ],
  );

  const paginationState = useMemo(
    () => ({
      pagination: {
        pageIndex: pageNumber - 1,
        pageSize,
      },
    }),
    [pageNumber, pageSize],
  );

  const hasCurrentPage = pages.some(p => getPageNumber(p) === pageNumber);

  const isLoadingState =
    isLoading ||
    isFetchingNextPage ||
    isFetchingPreviousPage ||
    (isFetching && !hasCurrentPage);

  if (!infiniteQuery) return null;

  const shouldRenderExternalEmptyState =
    !isLoadingState &&
    currentPageData.length === 0 &&
    emptyStateProps != null &&
    emptyOverlay == null;

  if (shouldRenderExternalEmptyState) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <EmptyState {...emptyStateProps} />
      </div>
    );
  }

  return (
    <DataTable<TData>
      {...rest}
      data={currentPageData}
      emptyOverlay={emptyOverlay}
      hideFooterOnSinglePage={rest.hideFooterOnSinglePage ?? true}
      loading={isLoadingState}
      manualPagination
      onPageChange={handlePageChange}
      pagination
      rowCount={totalCount}
      state={sorting ? { ...paginationState, sorting } : paginationState}
    />
  );
};

InfiniteQueryDataTableCompass.displayName = "InfiniteQueryDataTableCompass";
InfiniteQueryDataTableCompass.Wrapper = Wrapper;
